import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { api, extractApiError } from "@/lib/api";

export default function CocktailVote() {
  const { session_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/cocktail-vote/verify/${session_id}`).then(r => {
      if (cancelled) return;
      setState(r.data);
      if (r.data.already_voted) setDone(true);
    }).catch(err => {
      if (!cancelled) setError(extractApiError(err, "We couldn't find your order."));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [session_id]);

  const toggle = (key) => {
    setSelected(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= 3) { toast.error("You can pick up to 3 cocktails."); return prev; }
      return [...prev, key];
    });
  };

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least one cocktail."); return; }
    setSubmitting(true);
    try {
      await api.post("/cocktail-vote/submit", { session_id, cocktails: selected });
      setDone(true);
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't submit your vote."));
    } finally { setSubmitting(false); }
  };

  const wrap = "on-dark min-h-screen pt-32 md:pt-40 pb-24 px-5 md:px-10";
  const inner = "mx-auto max-w-[900px]";
  const bgStyle = { background: "var(--ss-bg-2)" };

  if (loading) return <div className={wrap} style={bgStyle} data-testid="vote-loading"><div className={inner}><p style={{ color: "var(--ss-cream-dim)" }}>Loading…</p></div></div>;
  if (error) return (
    <div className={wrap} style={bgStyle} data-testid="vote-error"><div className={inner}>
      <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Cocktail Vote</div>
      <h1 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">We couldn't find your ticket.</h1>
      <p className="mt-5 font-body text-base" style={{ color: "var(--ss-cream-dim)" }}>{error}</p>
      <p className="mt-3 font-body text-sm" style={{ color: "var(--ss-muted)" }}>Try opening the button in your confirmation email again.</p>
    </div></div>
  );
  if (!state?.voting_open) return (
    <div className={wrap} style={bgStyle} data-testid="vote-closed"><div className={inner}>
      <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Cocktail Vote</div>
      <h1 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">Voting is officially closed! 🍸</h1>
      <p className="mt-5 font-body text-lg" style={{ color: "var(--ss-cream-dim)" }}>Stay tuned for the winning cocktail lineup.</p>
    </div></div>
  );
  if (done) return (
    <div className={wrap} style={bgStyle} data-testid="vote-done"><div className={inner}>
      <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Vote Confirmed</div>
      <h1 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">Your vote is in! 🍸</h1>
      <p className="mt-5 font-body text-lg" style={{ color: "var(--ss-cream-dim)" }}>We'll reveal the winning cocktails before class. Get ready to shake, pour & sip!</p>
    </div></div>
  );

  return (
    <div className={wrap} style={bgStyle} data-testid="vote-page"><div className={inner}>
      <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Cocktail Vote</div>
      <h1 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
        You bought the ticket. Now help pick the menu! 🍸
      </h1>
      <p className="mt-6 font-body text-lg" style={{ color: "var(--ss-cream-dim)" }}>
        Choose up to <strong style={{ color: "var(--ss-cream)" }}>3 cocktails</strong> you'd most like to learn how to make.
      </p>
      <div className="mt-3 font-label text-xs" style={{ color: "var(--ss-gold)" }}>
        Selected {selected.length}/3
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="vote-options">
        {state.cocktails.map(c => {
          const on = selected.includes(c.key);
          return (
            <button key={c.key} type="button" onClick={() => toggle(c.key)}
                    data-testid={`vote-opt-${c.key}`}
                    className="flex items-center justify-between p-5 border text-left transition-colors"
                    style={{ borderColor: on ? "var(--ss-rust)" : "var(--ss-border)",
                             background: on ? "rgba(160,30,42,0.15)" : "transparent",
                             color: "var(--ss-cream)" }}>
              <span className="font-display text-xl">{c.name}</span>
              <span className="h-6 w-6 flex items-center justify-center border"
                    style={{ borderColor: on ? "var(--ss-rust)" : "var(--ss-cream-dim)",
                             background: on ? "var(--ss-rust)" : "transparent" }}>
                {on && <Check size={14} style={{ color: "#F5F1E8" }} />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-10">
        <button type="button" onClick={submit} disabled={submitting || selected.length === 0}
                data-testid="vote-submit" className="btn-primary">
          {submitting ? "Sending…" : `Submit My ${selected.length || ""} Pick${selected.length === 1 ? "" : "s"}`.trim()}
          <ArrowUpRight className="arrow" size={14} />
        </button>
        <p className="mt-4 font-label text-[0.6rem]" style={{ color: "var(--ss-muted)" }}>
          One vote per order. Results are kept private until class.
        </p>
      </div>
    </div></div>
  );
}
